import {
  Button,
  P,
  Pictogram,
  RadioCard,
  RadioField,
  Stack,
  Strong,
} from "@ovotech/element";
import * as React from "react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { Page, SplitHeading } from "_core/components";
import { FormApi, Formy } from "_core/form";
import { ProductUrls } from "_core/types";
import {
  FormValues,
  OffStreetParkingValidator,
} from "_core/validators/OffStreetParking";
import { useStore } from "_core/store";
import Mixpanel from "_core/analytics/Mixpanel";
import {
  AnalyticsEvents,
  DataLayerEvent,
} from "_core/analytics/AnalyticsEvents";
import { DataLayer } from "_core/analytics/DataLayer";
import { customizationPageForCharger } from "_core/utils/charger";

const OffStreetParkingPageContent = () => {
  const { setOffStreetParking } = useStore();
  const { charger, postcode, cableSetup, offStreetParking } = useStore();
  const [failure, setFailure] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (values: FormValues) => {
    setOffStreetParking(values.offStreetParking);

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: `offStreetParking-${values.offStreetParking}`,
      pageName: ProductUrls.OffStreetParking,
      charger,
    });

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "offStreetParking-submit-button",
      pageName: ProductUrls.OffStreetParking,
      charger,
    });

    DataLayer.push({
      event: DataLayerEvent.SubmitOffStreetParking,
      pageName: ProductUrls.OffStreetParking,
      charger,
    });

    if (values.offStreetParking === "no") {
      setFailure(true);
      return;
    }

    const nextPage = customizationPageForCharger(charger);
    if (nextPage) {
      navigate(nextPage);
    } else {
      navigate(ProductUrls.PersonalDetails);
    }
  };

  if (!postcode && !cableSetup) return <Navigate to={ProductUrls.Postcode} />;

  if (failure) {
    return <Navigate to={ProductUrls.KerboCharge} />;
  }

  return (
    <>
      <Formy
        initialValues={{
          offStreetParking: offStreetParking,
        }}
        validationSchema={OffStreetParkingValidator}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values,
          errors,
          setFieldValue,
        }: FormApi<FormValues>) => {
          return (
            <form data-testid="offStreetParking-form" onSubmit={handleSubmit}>
              <Stack spaceBetween={10}>
                <Stack spaceBetween={8}>
                  <SplitHeading part1="Get a" part2="home EV charger" />
                  <div>
                    <P size={"lg"}>Do you have off-street parking?</P>

                    <RadioField
                      className={"RadioField"}
                      id="offStreetParking"
                      name="offStreetParking"
                      label={<P>e.g. a driveway or garage</P>}
                      error={errors.offStreetParking}
                    >
                      <RadioCard
                        className={"RadioCard"}
                        name="offStreetParking"
                        value="yes"
                        id="offStreetParking-yes"
                        label={
                          <div>
                            <Pictogram
                              name="thumbs-up"
                              size="5em"
                              color="var(--color-brand-brand)"
                            />
                            <P size={"lg"}>
                              <Strong>Yes</Strong>
                            </P>
                          </div>
                        }
                        checked={values.offStreetParking === "yes"}
                        onChange={() =>
                          setFieldValue("offStreetParking", "yes", false)
                        }
                      />
                      <RadioCard
                        className={"RadioCard"}
                        name="offStreetParking"
                        value="no"
                        id="offStreetParking-no"
                        label={
                          <div>
                            <Pictogram
                              name="thumbs-down"
                              size="5em"
                              color="var(--color-brand-brand)"
                            />
                            <P size={"lg"}>
                              <Strong>No</Strong>
                            </P>
                          </div>
                        }
                        checked={values.offStreetParking === "no"}
                        onChange={() =>
                          setFieldValue("offStreetParking", "no", false)
                        }
                      />
                    </RadioField>
                  </div>
                </Stack>
                <div className={"ButtonContainer"}>
                  <Button
                    variant="outline"
                    iconLeft="chevron-left"
                    data-testid="offStreetParking-back-button"
                    onClick={() => navigate(ProductUrls.Postcode)}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    iconRight="chevron-right"
                    data-testid="offStreetParking-submit-button"
                  >
                    Next
                  </Button>
                </div>
              </Stack>
            </form>
          );
        }}
      </Formy>
    </>
  );
};

export const OffStreetParkingPage = () => {
  return (
    <Page displaySteps title="" subtitle="" url={ProductUrls.OffStreetParking}>
      <OffStreetParkingPageContent />
    </Page>
  );
};
